import React, {PureComponent} from "react";
import {connect, DispatchProp} from "react-redux";
import {loadScene} from "../../store/routing/actions";
import {IApplicationState} from "../../store";
import {BonusType} from "../../store/player/types";
import TopPanel from "../../shared/components/top-panel/TopPanel";
import {SceneryType} from "../../store/settings/SceneryType";
import Tile from "./Tile";
import styles from "./level-map.module.css";
import {Decal} from "./Decal";
import {
    IAggregatedQuestion,
    ICategory,
    ILevelMap,
    ILevelMapAggregatedTile,
    ILevelMapTileProgress,
    TileType
} from "../../store/game/types";
import {openTile, revealTile} from "../../store/game/actions";
import {hasLives} from "../../store/player/actions";
import HintPanel from "../../shared/components/hint-panel/HintPanel";
import {getLevelMapHints, getLevelMapHintsId} from "../../store/hints/types";
import {TweenLite, TimelineLite, Animation} from "gsap";
import {showDialog} from "../../store/dialog-manager/actions";
import playBackground from "../../shared/sounds/playBackground";
import {IAnimationSettings, ICanvasDimensions} from "../../store/settings/types";
import {AppScene} from "../../store/routing/AppScene";
import {showTutorial} from "../../store/tutorial/actions";
import playSound from "../../shared/sounds/playSound";
import {NotificationDialog} from "../../shared/components/dialog-manager/templates/notification-dialog/NotificationDialog";
import i18n from "../../shared/i18n";
import scrollbar from "../../shared/styles/scrollbars.module.css";
import isTouchDevice from "../../shared/utils/isTouchDevice";
import { cssHelper } from "../../shared/utils/cssHelper";

interface LevelMapSceneProps {
    readonly hasLives: boolean;
    readonly levelMap?: ILevelMap;
    readonly previousScene?: AppScene;
    readonly levelInProgress: number;
    readonly isCompleted: boolean;
    readonly levelScenery: SceneryType;
    readonly categories: Record<number, ICategory>;
    readonly questions: Record<number, IAggregatedQuestion>;
    readonly progress: Record<number, ILevelMapTileProgress>;
    readonly isRevealTileMode: boolean;
    readonly isExitFound: boolean;
    readonly allTilesAreRevealed: boolean;
    readonly canvas: ICanvasDimensions;
    readonly animation: IAnimationSettings;
}

const TOP_PANEL_HEIGHT = 130;
const BOTTOM_PANEL_HEIGHT = 130;
const TILE_OFFSET = 15;

let firstTimeShown = true;
let levelInProgress = -1;
let levelScrollTop = 0;

class LevelMapScene extends PureComponent<LevelMapSceneProps & DispatchProp> {
    private readonly scrollView: React.RefObject<HTMLDivElement>;
    private readonly canvas: React.RefObject<HTMLDivElement>;
    private readonly spacer: React.RefObject<HTMLDivElement>;
    private activeTween?: Animation;

    constructor(props: LevelMapSceneProps & DispatchProp, context: any) {
        super(props, context);

        this.scrollView = React.createRef();
        this.canvas = React.createRef();
        this.spacer = React.createRef();

        this.scrollHandler = this.scrollHandler.bind(this);
    }

    render() {
        const {levelInProgress, levelScenery, dispatch} = this.props;
        const levelRows = this.renderMapRows(levelScenery);
        if (!levelRows) {
            return this.renderNoMoreQuestionsMessage();
        }

        const decals = this.renderDecals(levelScenery);
        const animatePanels = this.props.previousScene === 'LevelSelection' || firstTimeShown;

        return (
            <section className={styles.container}>
                <TopPanel animate={animatePanels} onBackButtonClick={() => dispatch(loadScene.request("LevelSelection"))} />

                <div className={cssHelper(styles.scroll_view).add(isTouchDevice() ? scrollbar.empty : scrollbar.thin).toString()} ref={this.scrollView}>
                    <div className={cssHelper(styles.scroll_spacer).add(levelScenery + '_bg').toString()} ref={this.spacer}/>
                    <div className={styles.canvas} ref={this.canvas}>
                        <table className={styles.field}>
                            <tbody>
                            {levelRows}
                            </tbody>
                        </table>
                        <div className={styles.decal_container}>
                            {decals}
                        </div>
                    </div>
                </div>

                <HintPanel animate={animatePanels} delay={0.9} id={getLevelMapHintsId(levelInProgress)} hints={[BonusType.RevealTile, BonusType.RevealMap, BonusType.ShowExit]}/>
            </section>
        );
    }

    componentDidMount(): void {
        playBackground("level");

        this.initScrollView();

        const routeFromLevelSelection = this.props.previousScene === 'LevelSelection' || firstTimeShown;
        const animateTiles = routeFromLevelSelection && this.props.animation.enabled;
        firstTimeShown = false;

        if (animateTiles) {
            this.animateTiles();
        }

        if (this.scrollView.current) {
            this.props.dispatch(showTutorial.request({
                selector: `.${styles.tile_clickable}`,
                title: "gameplay.firstquestiontutor",
                delay: animateTiles ? 1.0 : 0
            }));
        }
    }

    componentDidUpdate(prevProps: Readonly<LevelMapSceneProps & DispatchProp>, prevState: Readonly<{}>, snapshot?: any): void {
        if (this.props.isExitFound && !prevProps.isExitFound && this.scrollView.current) {
            this.props.dispatch(showTutorial.request({
                selector: `.${styles.exit_icon}`,
                title: "gameplay.levelmap.exitfoundtutorial",
                delay: this.props.animation.enabled ? 1.0 : 0.2,
                closeOnTouch: true,
                onShow: () => {
                    if (this.scrollView.current) {
                        const exitIcon = this.scrollView.current.getElementsByClassName(styles.exit_icon)[0] as HTMLElement;
                        if (exitIcon) {
                            TweenLite.to(this.scrollView.current, 0.33, {
                                scrollTop: this.getTileScrollPosition(exitIcon.parentElement!)
                            });
                        }
                    }
                }
            }));
        }
    }

    componentWillUnmount(): void {
        this.activeTween && this.activeTween.kill();

        if (this.scrollView.current) {
            this.scrollView.current.removeEventListener('scroll', this.scrollHandler, false);
        }
    }

    private renderMapRows(scenery: SceneryType) {
        const {levelMap, isExitFound, allTilesAreRevealed, canvas, animation} = this.props;

        if (!levelMap || !levelMap.tiles) {
            return;
        }

        return levelMap.tiles.map((row: ILevelMapAggregatedTile[], rowNumber: number) => {
            return (<tr key={rowNumber}>
                {row.map((tile: ILevelMapAggregatedTile, i) => {
                    if (!tile.id) {
                        return (<td key={i}> </td>);
                    }

                    const progress = this.props.progress[tile.id];
                    const question = tile.questions.map(questionId => this.props.questions[questionId]).shift();
                    const category = question && question.categoryId && this.props.categories
                        ? this.props.categories[question.categoryId]
                        : undefined;

                    const aggregatedTile = {...tile, ...progress};
                    const onClickHandler = this.getOnTileClickHandler(aggregatedTile);

                    return <td key={`${tile.id}`}>
                        <Tile {...tile} {...progress} question={question} category={category} scenery={scenery} isClickable={!!onClickHandler} isExitFound={isExitFound} allTilesAreRevealed={allTilesAreRevealed} onClick={onClickHandler} animation={animation} scaleFactor={canvas.scaleFactor}/>
                    </td>;
                })}
            </tr>);
        });
    }

    private isClickable(tile: ILevelMapAggregatedTile & ILevelMapTileProgress) {
        const {hasLives, isExitFound, isCompleted} = this.props;

        return tile.isRevealed
            && !tile.isAnswered
            && tile.tileType
            && (tile.tileType !== TileType.Exit || isExitFound)
            && !isCompleted
            && hasLives;
    }

    private getOnTileClickHandler(tile: ILevelMapAggregatedTile & ILevelMapTileProgress) {
        const {hasLives, isRevealTileMode, dispatch} = this.props;

        if (isRevealTileMode)
            return () => dispatch(revealTile(tile));

        if (this.isClickable(tile))
            return () => dispatch(openTile(tile));

        if (!hasLives)
            return () => dispatch(showDialog({ type: 'NoLivesDialog', options: {} }));

        return undefined;
    }

    private renderDecals(scenery: SceneryType) {
        if (!this.props.levelMap || !this.props.levelMap.decals)
            return;

        return this.props.levelMap.decals.map((decal, index) => <Decal key={index} {...decal} scenery={scenery}/>)
    }

    private animateTiles() {
        if (!this.canvas.current)
            return;

        const {animation} = this.props;

        const tiles = this.canvas.current.querySelectorAll('[data-animated]');

        this.activeTween = new TimelineLite()
            .staggerFrom(tiles, 0.33, {
                scale: 0,
                cycle: {
                    delay: (i: number, target: HTMLElement) => {
                        const multiplier = target.dataset.animated && parseFloat(target.dataset.animated) || 1;

                        return Math.random() * multiplier;
                    }
                }
            }, 0)
            .timeScale(animation.timeScale);

        let tileCount = Math.min(10, tiles.length);
        const tileSoundTimer = setInterval(() => {
            playSound("tile-set-in");
            if (!tileCount--) {
                clearInterval(tileSoundTimer);
            }
        }, 100 / animation.timeScale);
    }

    private initScrollView() {
        if (!this.scrollView.current || !this.canvas.current)
            return;

        const padding = TOP_PANEL_HEIGHT + BOTTOM_PANEL_HEIGHT;
        const canvasHeight = this.canvas.current.offsetHeight;
        const scrollHeight = Math.max(this.canvas.current.offsetHeight + padding, this.scrollView.current.offsetHeight);

        if (this.spacer.current) {
            this.spacer.current.style.height = scrollHeight + "px";
        }

        this.canvas.current.style.top = ((scrollHeight- canvasHeight) / 2) + "px";

        if (this.props.levelInProgress === levelInProgress) {
            this.scrollView.current.scrollTop = levelScrollTop;
        } else {
            levelInProgress = this.props.levelInProgress;

            const revealedTile = this.scrollView.current.getElementsByClassName(styles.tile_clickable)[0] as HTMLElement;
            this.scrollView.current.scrollTop = levelScrollTop = this.getTileScrollPosition(revealedTile);
        }

        this.scrollView.current.addEventListener('scroll', this.scrollHandler, false);
    }

    private getTileScrollPosition(tile: HTMLElement) {
        if (!this.scrollView.current || !this.canvas.current)
            return 0;

        const padding = TOP_PANEL_HEIGHT + BOTTOM_PANEL_HEIGHT;
        const scrollHeight = Math.max(this.canvas.current.offsetHeight + padding, this.scrollView.current.offsetHeight);

        const visibleArea = this.scrollView.current.offsetHeight - padding;
        const middlePointScrollTop = (scrollHeight - this.scrollView.current.offsetHeight) / 2;

        if (tile) {
            const revealedTileTop = tile.getBoundingClientRect().top / this.props.canvas.scaleFactor - TOP_PANEL_HEIGHT;

            return middlePointScrollTop > revealedTileTop - TILE_OFFSET
                ? revealedTileTop - TILE_OFFSET
                : middlePointScrollTop + visibleArea < revealedTileTop + tile.offsetHeight + TILE_OFFSET
                    ? revealedTileTop + tile.offsetHeight - visibleArea + TILE_OFFSET
                    : middlePointScrollTop;
        } else {
            return levelScrollTop = middlePointScrollTop;
        }
    }

    private scrollHandler() {
        if (!this.scrollView.current)
            return;

        levelScrollTop = this.scrollView.current.scrollTop;
    }

    private renderNoMoreQuestionsMessage() {
        const {animation} = this.props;

        return (
            <NotificationDialog
                text={i18n("nomorequestions")}
                animation={animation}
                close={() => this.props.dispatch(loadScene.request("MainMenu"))}
            />
        );
    }
}

export default connect(({ game, hints, player, settings, routing }: IApplicationState): LevelMapSceneProps => {
    const levelProgress = game.levelMapProgress[game.levelInProgress];

    return {
        hasLives: hasLives(player),
        levelInProgress: game.levelInProgress,
        previousScene: routing.previousScene,
        isExitFound: levelProgress.isExitFound,
        allTilesAreRevealed: levelProgress.allTilesAreRevealed,
        isCompleted: levelProgress.isCompleted,
        levelScenery: game.levelScenery,
        levelMap: game.levelInProgress > -1 ? game.levelMaps[game.levelInProgress] : undefined,
        progress: game.levelMapTileProgress,
        questions: game.questions,
        categories: game.categories,
        isRevealTileMode: getLevelMapHints(hints.activeHints, game.levelInProgress).includes(BonusType.RevealTile),
        canvas: settings.canvas,
        animation: settings.animation,
    };
})(LevelMapScene);